import React, { type JSX } from 'react';
import Table, { type TableColumn } from '../Table/Table';
import VerticalContent from '../VerticalContent/VerticalContent';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import {
  IconDefaultsContextProvider,
  iconSizeExtraLarge,
} from '../../contexts/useIconDefaults';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import useTableHiddenColumns from '@resources/js/contexts/useTableHiddenColumns';
import cx from 'classnames';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export type BuildsEnchantsTablesViewModel = {
  title: string;
  description: string;
  tables: Array<{
    columns: Record<
      'item' | 'popularity',
      BuildsEnchantsTablesSectionViewModelColumn
    > &
      Partial<
        Record<
          'dps' | 'hps' | 'maxKey',
          BuildsEnchantsTablesSectionViewModelColumn
        >
      >;
    data: Array<{
      item: string;
      maxKey?: string;
      dps?: string;
      hps?: string;
      popularity: string;
    }>;
  }>;
};

type BuildsEnchantsTablesSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  icon: {
    size: iconSizeExtraLarge,
    rounded: false,
    bordered: true,
  },
  item: { size: 28 },
  ability: { size: 28 },
};

export default function BuildsEnchantTablesSection({
  title,
  description,
  tables,
}: BuildsEnchantsTablesViewModel): JSX.Element {
  const { hiddenColumns } = useTableHiddenColumns();
  const numberOfVisibleColumns =
    tables.length === 0
      ? 0
      : Object.keys(tables[0].columns).filter(
          (column) => !hiddenColumns.includes(column),
        ).length;
  const isWide = numberOfVisibleColumns > 3;

  return (
    <VerticalContent
      className={cx({
        'builds-enchants-tables-section': true,
        'builds-enchants-tables-section--wide': isWide,
      })}
      gap={'large'}
    >
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {tables.length > 0 ? (
          <div className='builds-enchants-tables-section__group'>
            {tables.map((table, index) => {
              return (
                <Table
                  key={index}
                  {...{
                    ...table,
                    columns: {
                      ...table.columns,
                      item: {
                        ...table.columns.item,
                        maxWidth: 220,
                      },
                    },
                  }}
                  enhancedMarkdownEnabled
                  style='v2-bordered'
                  truncate={{
                    limit: 3,
                    showMoreButton: { styles: ['link', 'bold'] },
                  }}
                />
              );
            })}
          </div>
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
